"use client"

import React, { createContext, useContext, useReducer, useCallback, ReactNode, useEffect } from 'react';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where,
  Timestamp, runTransaction,
  setDoc,
  getDoc,
  increment,
  writeBatch
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { useBudgetManager } from './BudgetManagerContext';
import { useInventory, InventoryItem } from './InventoryContext';
import { useAccount } from './AccountContext';
import { useTransaction } from './TransactionContext';
import { am } from 'vitest/dist/reporters-5f784f42.js';
import schedulerService from '@/services/scheduler/schedularService';
import { CompanySettings, useCompany } from './CompanyContext';

// Define types
export type InvoiceItem = {
  name: string;
  inventoryItemId: string;
  quantity: number;
  price: number;
};

export type Invoice = {
  id?: string;
  invoiceNumber: number;
  customerId: string;
  customerName: string;
  customerEmail: string;
  dueDate: Date;
  items: InvoiceItem[];
  taxRate: number;
  notes: string;
  status: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  amount: number;
  budgetId: string;
  accountId: string;
  category?: string;
  parentCategory?: string;
  reminderScheduled?: boolean;
  enableReminders?: boolean;
  reminderSettings?: {
    firstReminder: number;
    subsequentReminders: number;
    maxReminders: number;
  };
  paymentMethod?: string;
  paymentTerms?: string;
  bankDetails?: string;
  accessToken?: string;
  companyId?: string;
};

type InvoiceState = {
  invoices: Invoice[];
  loading: boolean;
  error: string | null;
};

type InvoiceAction =
  | { type: 'LOAD_INVOICES'; payload: Invoice[] }
  | { type: 'ADD_INVOICE'; payload: Invoice }
  | { type: 'UPDATE_INVOICE'; payload: Invoice }
  | { type: 'DELETE_INVOICE'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

type InvoiceContextType = {
  state: InvoiceState;
  loadInvoices: () => Promise<void>;
  addInvoice: (invoice: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt' | 'invoiceNumber'>) => Promise<void>;
  updateInvoice: (invoice: Invoice) => Promise<void>;
  deleteInvoice: (invoiceId: string) => Promise<void>;
  clearError: () => void;
  scheduleReminders: (invoice: Invoice, companySettings: CompanySettings) => Promise<void>;
  updateReminderStatus: (invoiceId: string, status: {
    lastReminderSent?: Date;
    nextReminderDate?: Date;
    reminderCount?: number;
  }) => Promise<void>;
};

// Create context
const InvoiceContext = createContext<InvoiceContextType | undefined>(undefined);

// Initial state
const initialState: InvoiceState = {
  invoices: [],
  loading: false,
  error: null,
};

const generateAccessToken = () => {
  return crypto.randomUUID();
};

export const getInvoicePublicUrlToken = (invoice: Invoice) => {
  if (!invoice.accessToken) return null;
  return `?token=${invoice.accessToken}`;
};

// Reducer function
function invoiceReducer(state: InvoiceState, action: InvoiceAction): InvoiceState {
  switch (action.type) {
    case 'LOAD_INVOICES':
      return { ...state, invoices: action.payload, loading: false };
    case 'ADD_INVOICE':
      return { ...state, invoices: [...state.invoices, action.payload], loading: false };
    case 'UPDATE_INVOICE':
      return {
        ...state,
        invoices: state.invoices.map((invoice) =>
          invoice.id === action.payload.id ? action.payload : invoice
        ),
        loading: false,
      };
    case 'DELETE_INVOICE':
      return {
        ...state,
        invoices: state.invoices.filter((invoice) => invoice.id !== action.payload),
        loading: false,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
}

// Provider component
export function InvoiceProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(invoiceReducer, initialState);
  const { state: budgetState } = useBudgetManager();
  const { state: inventoryState, loadInventoryItems } = useInventory();
  const { state: accountState } = useAccount();
  const { addTransaction } = useTransaction();
  const { state: companyState } = useCompany();

  const scheduleReminders = useCallback(async (invoice: Invoice, companySettings: CompanySettings) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      if (!invoice.id) throw new Error('Invoice ID is required for scheduling reminders');

      // Schedule reminders using the schedulerService
      const scheduled = await schedulerService.scheduleReminders(invoice, 'invoice', companySettings);

      // Update invoice with reminder scheduled status
      await updateDoc(doc(db, 'invoices', invoice.id), {
        reminderScheduled: true,
        updatedAt: Timestamp.now()
      });

      // Update local state
      const updatedInvoice = {
        ...invoice,
        reminderScheduled: true,
        updatedAt: new Date()
      };
      dispatch({ type: 'UPDATE_INVOICE', payload: updatedInvoice });

    } catch (error) {
      console.error('Error scheduling reminders:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to schedule reminders' });
      throw error;
    }
  }, []);

  const updateReminderStatus = useCallback(async (
    invoiceId: string,
    status: {
      lastReminderSent?: Date;
      nextReminderDate?: Date;
      reminderCount?: number;
    }
  ) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const updateData: any = {
        updatedAt: Timestamp.now()
      };

      if (status.lastReminderSent) {
        updateData.lastReminderSent = Timestamp.fromDate(status.lastReminderSent);
      }
      if (status.nextReminderDate) {
        updateData.nextReminderDate = Timestamp.fromDate(status.nextReminderDate);
      }
      if (typeof status.reminderCount === 'number') {
        updateData.reminderCount = status.reminderCount;
      }

      await updateDoc(doc(db, 'invoices', invoiceId), updateData);

      // Update local state
      const existingInvoice = state.invoices.find(i => i.id === invoiceId);
      if (existingInvoice) {
        const updatedInvoice = {
          ...existingInvoice,
          ...status,
          updatedAt: new Date()
        };
        dispatch({ type: 'UPDATE_INVOICE', payload: updatedInvoice });
      }

    } catch (error) {
      console.error('Error updating reminder status:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update reminder status' });
      throw error;
    }
  }, [state.invoices]);

  // Add handleReminderResponse function to process customer responses
  const handleReminderResponse = async (invoiceId: string, response: 'acknowledged' | 'disputed' | 'will_pay') => {
    try {
      const invoice = state.invoices.find(i => i.id === invoiceId);
      if (!invoice) throw new Error('Invoice not found');

      const updateData: any = {
        updatedAt: Timestamp.now(),
        lastResponseStatus: response,
        lastResponseDate: Timestamp.now()
      };

      if (response === 'will_pay') {
        // Maybe extend the due date or add a promised payment date
        const newDueDate = new Date();
        newDueDate.setDate(newDueDate.getDate() + 7); // Give one week extension
        updateData.dueDate = Timestamp.fromDate(newDueDate);
      }

      await updateDoc(doc(db, 'invoices', invoiceId), updateData);

      const updatedInvoice = {
        ...invoice,
        ...updateData,
        updatedAt: new Date()
      };
      dispatch({ type: 'UPDATE_INVOICE', payload: updatedInvoice });

    } catch (error) {
      console.error('Error handling reminder response:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to process reminder response' });
      throw error;
    }
  };

  const loadInvoices = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetState.currentBudget;

      if (!currentBudget || !currentBudget.id) {
        console.warn('No budget selected or budget has no ID');
        dispatch({ type: 'LOAD_INVOICES', payload: [] });
        return;
      }

      const invoicesQuery = query(
        collection(db, 'invoices'),
        where('createdBy', '==', user.uid),
        where('budgetId', '==', currentBudget.id)
      );

      const querySnapshot = await getDocs(invoicesQuery);
      const invoices: Invoice[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          dueDate: data.dueDate.toDate(),
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        } as Invoice;
      });

      dispatch({ type: 'LOAD_INVOICES', payload: invoices });
    } catch (error) {
      console.error('Error loading invoices:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load invoices' });
    }
  }, [budgetState.currentBudget]);

  const getNextInvoiceNumber = async (budgetId: string): Promise<number> => {
    const counterRef = doc(db, 'invoice_counters', budgetId);

    try {
      const newNumber = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);

        if (!counterDoc.exists()) {
          // Initialize the counter if it doesn't exist
          transaction.set(counterRef, {
            currentNumber: 1,
            budgetId: budgetId,
            createdBy: auth.currentUser?.uid,
            updatedAt: Timestamp.now()
          });
          return 1;
        }

        const currentNumber = counterDoc.data().currentNumber || 0;
        const nextNumber = currentNumber + 1;

        transaction.update(counterRef, {
          currentNumber: nextNumber,
          updatedAt: Timestamp.now()
        });

        return nextNumber;
      });

      return newNumber;
    } catch (error) {
      console.error("Error getting next invoice number:", error);
      // Initialize counter if transaction failed due to missing document
      try {
        await setDoc(counterRef, {
          currentNumber: 1,
          budgetId: budgetId,
          createdBy: auth.currentUser?.uid,
          updatedAt: Timestamp.now()
        });
        return 1;
      } catch (fallbackError) {
        console.error("Fallback error:", fallbackError);
        throw error;
      }
    }
  };

  const addInvoice = useCallback(async (invoice: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt' | 'invoiceNumber'>) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetState.currentBudget;
      if (!currentBudget) throw new Error('No budget selected');

      // Get the next invoice number
      const nextInvoiceNumber = await getNextInvoiceNumber(currentBudget.id);

      // Generate access token
      const accessToken = generateAccessToken();

      // Validate account
      const account = accountState.accounts.find(a => a.id === invoice.accountId);
      if (!account) {
        throw new Error(`Account not found: ${invoice.accountId}`);
      }

      // Validate inventory items and calculate total
      const inventoryUpdates: { id: string; quantity: number }[] = [];
      let totalAmount = 0;
      const itemsWithDetails: InvoiceItem[] = [];

      for (const item of invoice.items) {
        const inventoryItem = inventoryState.items.find(i => i.id === item.inventoryItemId);
        if (!inventoryItem) {
          throw new Error(`Inventory item not found: ${item.inventoryItemId}`);
        }
        if (inventoryItem.quantity < item.quantity) {
          throw new Error(`Insufficient quantity for item: ${inventoryItem.name}. Available: ${inventoryItem.quantity}, Requested: ${item.quantity}`);
        }

        // Track inventory updates needed
        inventoryUpdates.push({
          id: inventoryItem.id,
          quantity: inventoryItem.quantity - item.quantity
        });

        // Calculate item total and add to total amount
        const itemTotal = item.quantity * item.price;
        totalAmount += itemTotal;

        // Store item with complete details
        itemsWithDetails.push({
          ...item,
          name: inventoryItem.name // Ensure we store the current item name
        });
      }

      // Apply tax if specified
      if (invoice.taxRate > 0) {
        const taxAmount = (totalAmount * invoice.taxRate) / 100;
        totalAmount += taxAmount;
      }

      // Prepare items description for transaction
      const itemsDescription = itemsWithDetails
        .map(item => `${item.name} x${item.quantity}`)
        .join(', ');

      // Prepare new invoice document
      const newInvoice = {
        ...invoice,
        items: itemsWithDetails,
        invoiceNumber: nextInvoiceNumber,
        amount: totalAmount,
        createdBy: user.uid,
        budgetId: currentBudget.id,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        reminderScheduled: false,
        reminderCount: 0,
        reminderSettings: invoice.reminderSettings || {
          firstReminder: 1,
          subsequentReminders: 7,
          maxReminders: 3
        },
        accessToken,
        companyId: companyState.settings?.id
      };

      // Start a Firestore batch
      const batch = writeBatch(db);

      // Add the invoice document
      const invoiceRef = doc(collection(db, 'invoices'));
      batch.set(invoiceRef, newInvoice);

      // Update inventory quantities
      for (const update of inventoryUpdates) {
        const inventoryRef = doc(db, 'inventory_items', update.id);
        batch.update(inventoryRef, {
          quantity: update.quantity,
          updatedAt: Timestamp.now()
        });
      }

      // If invoice is marked as paid, create transaction
      if (invoice.status === 'paid') {
        const transactionRef = doc(collection(db, 'transactions'));
        const transactionData = {
          description: `Invoice #${nextInvoiceNumber}: ${itemsDescription};\nInvoice for ${invoice.customerName}`,
          amount: totalAmount,
          date: Timestamp.now(),
          category: invoice.category || 'sales',
          parentCategory: invoice.parentCategory || 'income',
          type: 'income',
          accountId: invoice.accountId,
          createdBy: user.uid,
          budgetId: currentBudget.id,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          invoiceId: invoiceRef.id
        };
        batch.set(transactionRef, transactionData);

        // Update account balance
        const accountRef = doc(db, 'accounts', invoice.accountId);
        batch.update(accountRef, {
          balance: increment(totalAmount),
          availableToBudget: increment(totalAmount),
          updatedAt: Timestamp.now()
        });
      }

      // Commit all the changes
      await batch.commit();

      // Get company settings for reminder configuration
      const settingsQuery = query(
        collection(db, 'company_settings'),
        where('budgetId', '==', currentBudget.id),
        where('createdBy', '==', user.uid)
      );
      const settingsSnapshot = await getDocs(settingsQuery);

      if (!settingsSnapshot.empty) {
        const companySettings = settingsSnapshot.docs[0].data() as CompanySettings;

        // Schedule reminders if automatic reminders are enabled
        if (companySettings.invoiceSettings?.reminderSettings?.autoSendReminders) {
          await schedulerService.scheduleReminders({
            ...newInvoice,
            id: invoiceRef.id,
            createdAt: new Date(),
            updatedAt: new Date()
          }, 'invoice', companySettings);

          // Update invoice with reminder scheduled status
          await updateDoc(invoiceRef, {
            reminderScheduled: true,
            updatedAt: Timestamp.now()
          });
        }
      }

      // Update local state with the new invoice
      const addedInvoice: Invoice = {
        ...newInvoice,
        id: invoiceRef.id,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      dispatch({ type: 'ADD_INVOICE', payload: addedInvoice });

      // Refresh inventory state
      await loadInventoryItems();

    } catch (error) {
      console.error('Error adding invoice:', error);
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to add invoice' });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [
    budgetState.currentBudget,
    inventoryState.items,
    accountState.accounts,
    loadInventoryItems,
    getNextInvoiceNumber
  ]);

  const updateInvoice = useCallback(async (invoice: Invoice) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      if (!invoice.id) throw new Error('Invoice ID is required for update');
  
      const existingInvoice = state.invoices.find(i => i.id === invoice.id);
      if (!existingInvoice) throw new Error('Invoice not found');
  
      // Calculate total amount
      const totalAmount = invoice.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      let finalAmount = totalAmount;
      if (invoice.taxRate > 0) {
        finalAmount += (totalAmount * invoice.taxRate) / 100;
      }
  
      // Handle status change from paid to pending/overdue
      if (existingInvoice.status === 'paid' &&
          (invoice.status === 'pending' || invoice.status === 'overdue')) {
        
        // Find related transaction
        const transactionsRef = collection(db, 'transactions');
        const q = query(
          transactionsRef,
          where('invoiceId', '==', invoice.id),
          where('budgetId', '==', invoice.budgetId),
          where('type', '==', 'income')
        );
  
        const transactionSnapshot = await getDocs(q);
        
        if (!transactionSnapshot.empty) {
          const transactionDoc = transactionSnapshot.docs[0];
          
          // Create batch for atomic operations
          const batch = writeBatch(db);
  
          // Delete transaction
          batch.delete(doc(db, 'transactions', transactionDoc.id));
  
          // Update account balance
          const accountRef = doc(db, 'accounts', invoice.accountId);
          batch.update(accountRef, {
            balance: increment(-finalAmount),
            availableToBudget: increment(-finalAmount),
            updatedAt: Timestamp.now()
          });
  
          // Update invoice
          const invoiceRef = doc(db, 'invoices', invoice.id);
          batch.update(invoiceRef, {
            ...invoice,
            amount: finalAmount,
            updatedAt: Timestamp.now()
          });
  
          // Commit the batch
          await batch.commit();
        }
      } 
      // Handle status change from pending/overdue to paid
      else if (existingInvoice.status !== 'paid' && invoice.status === 'paid') {
        // Create batch for atomic operations
        const batch = writeBatch(db);
  
        // Create transaction
        const transactionRef = doc(collection(db, 'transactions'));
        const transactionData = {
          description: `Invoice #${invoice.invoiceNumber} payment`,
          amount: finalAmount,
          type: 'income',
          category: invoice.category || 'sales',
          parentCategory: invoice.parentCategory || 'income',
          date: Timestamp.now(),
          accountId: invoice.accountId,
          budgetId: invoice.budgetId,
          invoiceId: invoice.id,
          createdBy: auth.currentUser?.uid,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        };
        
        batch.set(transactionRef, transactionData);
  
        // Update account balance
        const accountRef = doc(db, 'accounts', invoice.accountId);
        batch.update(accountRef, {
          balance: increment(finalAmount),
          availableToBudget: increment(finalAmount),
          updatedAt: Timestamp.now()
        });
  
        // Update invoice
        const invoiceRef = doc(db, 'invoices', invoice.id);
        batch.update(invoiceRef, {
          ...invoice,
          amount: finalAmount,
          updatedAt: Timestamp.now()
        });
  
        // Commit the batch
        await batch.commit();
      } else {
        // Just update the invoice if no status change
        await updateDoc(doc(db, 'invoices', invoice.id), {
          ...invoice,
          amount: finalAmount,
          updatedAt: Timestamp.now()
        });
      }
  
      // Update local state
      dispatch({ type: 'UPDATE_INVOICE', payload: { ...invoice, amount: finalAmount } });
      
    } catch (error) {
      console.error('Error updating invoice:', error);
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to update invoice' });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.invoices]);

  const deleteInvoice = useCallback(async (invoiceId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      await deleteDoc(doc(db, 'invoices', invoiceId));
      dispatch({ type: 'DELETE_INVOICE', payload: invoiceId });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete invoice' });
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        loadInvoices();
      } else {
        dispatch({ type: 'LOAD_INVOICES', payload: [] });
      }
    });

    return () => unsubscribe();
  }, [loadInvoices]);

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  return (
    <InvoiceContext.Provider
      value={{
        state,
        loadInvoices,
        addInvoice,
        updateInvoice,
        deleteInvoice,
        clearError,
        scheduleReminders,
        updateReminderStatus,
      }}
    >
      {children}
    </InvoiceContext.Provider>
  );
}

// Custom hook to use the invoice context
export function useInvoice(): InvoiceContextType {
  const context = useContext(InvoiceContext);
  if (!context) {
    throw new Error('useInvoice must be used within an InvoiceProvider');
  }
  return context;
}